import type {
  EquitySimulationInput,
  EquitySimulationResult,
  EquitySimulatorCurvePoint,
} from "@/types/tradingTool";

const DEFAULT_STARTING_BALANCE = 50_000;
const CHART_BAR_COUNT = 10;

function clampPositive(value: number, fallback: number) {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

export function simulateEquityGrowth(input: EquitySimulationInput): EquitySimulationResult | null {
  const startingBalance = clampPositive(
    input.startingBalance ?? DEFAULT_STARTING_BALANCE,
    DEFAULT_STARTING_BALANCE,
  );
  const riskPercent = clampPositive(input.riskPercent, 0);
  const winRatePercent = Math.min(100, Math.max(0, input.winRatePercent));
  const rewardRatio = clampPositive(input.rewardRatio, 0);
  const numTrades = Math.round(clampPositive(input.numTrades, 0));

  if (riskPercent <= 0 || rewardRatio <= 0 || numTrades <= 0) {
    return null;
  }

  const risk = riskPercent / 100;
  const winRate = winRatePercent / 100;
  const curve: EquitySimulatorCurvePoint[] = [{ trade: 0, balance: startingBalance }];

  let balance = startingBalance;

  for (let trade = 1; trade <= numTrades; trade += 1) {
    const winAmount = balance * risk * rewardRatio;
    const lossAmount = balance * risk;
    balance += winRate * winAmount - (1 - winRate) * lossAmount;
    balance = Math.max(0, balance);
    curve.push({ trade, balance });
  }

  const projectedBalance = curve[curve.length - 1]?.balance ?? startingBalance;
  const expectedGainPercent =
    startingBalance > 0
      ? ((projectedBalance - startingBalance) / startingBalance) * 100
      : 0;

  const chartBars = Array.from({ length: CHART_BAR_COUNT }, (_, index) => {
    const tradeNumber = Math.max(
      1,
      Math.round((numTrades / CHART_BAR_COUNT) * (index + 1)),
    );
    const point = curve[tradeNumber];
    return {
      label: String(tradeNumber),
      value: point?.balance ?? startingBalance,
    };
  });

  return {
    startingBalance,
    curve,
    projectedBalance,
    expectedGainPercent,
    chartBars,
  };
}

export function formatUsd(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatGainPercent(value: number) {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}
